import { Select } from '../common/LeboncoinSelect';
import { Toggle } from '../common/Toggle';
import { TextInput } from '../common/TextInput';

type AnswerModelization = {
  representation: {
    associated_key: string;
    codec_type: string;
  };
  single_answer?: {
    type: string;
    default_answer?: string;
  };
  multiple_answers?: {
    choices?: Array<{
      identifier: string;
      label: string;
    }>;
    grouped_answers?: Array<{
      choices: Array<{
        identifier: string;
        label: string;
      }>;
    }>;
  };
};

type Decoration = {
  visual_representation: string;
  label?: string;
  helper_text?: {
    body: string[];
  };
  max_length?: number;
  is_max_length_visible?: boolean;
  title?: string;
  helper_modals?: Array<{
    type: string;
    data: {
      link_text: string;
    };
  }>;
  unit?: string;
  anchor?: string;
  badge?: string;
  chip?: string;
  placeholder?: string;
};

type StaticRules = {
  mandatory?: {
    error_message: string;
  };
  regex?: Array<{
    value: string;
    error_message: string;
  }>;
};

type DynamicRules = {
  conditional_existence?: Array<{
    existence_requirements: Array<{
      previous_associated_key?: string;
      previous_raw_answer?: string[];
      operator?: string;
      custom_type?: string;
    }>;
  }>;
  conditional_choices?: Array<{
    choices: Array<{
      identifier: string;
      label: string;
    }>;
    choices_requirements: Array<{
      previous_associated_key: string;
      previous_raw_answer: string[];
      operator: string;
    }>;
  }>;
};

type FormItem = {
  identifier: string;
  type: string;
  answer_modelization?: AnswerModelization;
  decoration: Decoration;
  static_rules?: StaticRules;
  dynamic_rules?: DynamicRules;
};

type FormConfig = FormItem[];

function createFormField(item: FormItem) {
  const { type, decoration, answer_modelization, dynamic_rules } = item;

  // Skip hidden fields
  if (decoration.visual_representation === 'DECORATION_VISUAL_REPRESENTATION_HIDDEN') {
    return null;
  }

  // Extract dynamic visibility rules if they exist
  const visibilityRules = dynamic_rules?.conditional_existence?.[0]?.existence_requirements;

  const commonProps = {
    value_key: answer_modelization?.representation.associated_key,
    visibilityRules: visibilityRules || [],
  };

  switch (type) {
    case 'ITEM_TYPE_QUESTION':
      if (answer_modelization?.multiple_answers || dynamic_rules?.conditional_choices) {
        return {
          Component: Select,
          props: {
            ...commonProps,
            label: decoration.label || '',
            options: (
              answer_modelization?.multiple_answers?.choices
              || answer_modelization?.multiple_answers?.grouped_answers?.flatMap(group => group.choices)
              || []
            ),
            helperText: decoration.helper_text?.body.join(' '),
            required: !!item.static_rules?.mandatory,
            error: item.static_rules?.mandatory?.error_message,
            dynamicChoices: dynamic_rules?.conditional_choices || []
          }
        };
      } else if (answer_modelization?.single_answer?.type === 'QUESTION_ANSWER_TYPE_BOOL') {
        return {
          Component: Toggle,
          props: {
            ...commonProps,
            label: decoration.label || '',
            defaultValue: answer_modelization.single_answer.default_answer === 'true',
            helperText: decoration.helper_text?.body.join(' '),
          }
        };
      } else if (answer_modelization?.single_answer?.type === 'QUESTION_ANSWER_TYPE_STRING') {
        return {
          Component: TextInput,
          props: {
            ...commonProps,
            label: decoration.label || '',
            maxLength: decoration.max_length,
            showMaxLength: decoration.is_max_length_visible,
            helperText: decoration.helper_text?.body.join(' '),
            required: !!item.static_rules?.mandatory,
            error: item.static_rules?.mandatory?.error_message,
          }
        };
      }
      break;

    case 'ITEM_TYPE_STATIC_VIEW':
      return {
        type: 'static',
        content: decoration.helper_text?.body.join(' ') || '',
        identifier: item.identifier,
        visibilityRules: visibilityRules || []
      };
  }

  return null;
}

function createForm(
  fieldsToDisplay: string[],
  fieldsDefinition: FormConfig
) {
  const form = fieldsToDisplay
      .map(itemId => {
        const item = fieldsDefinition.find(i => i.identifier === itemId);
        if (!item) return null;
        return createFormField(item);
      })
      .filter(field => field !== null)
  return form;
}

export default createForm;
